import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { OutputConfigSlice } from "../../redux";
import { OutputConfigBandMap } from "../../Api/Types";
import { OutputBandingMappingFormView } from "../OutputMappingForm/OutputMappingForm.view";
import {
  FormSection,
  FormElement,
  Form,
  Input
} from "../../../../Common/UI/Index";
import { InputType } from "../../../../Common/UI/Types";
import { BandFormTypes } from "./Types";

interface Props {
  /**
   * Config id of selected map
   */
  configId: string;
  /**
   * Data from api response
   */
  data: BandFormTypes[];
}

/**
 * Responsible for rendering age popup view
 */
export const OutputMappingAgeFormView: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { data } = props;
  const { t: translate } = useTranslation();
  const [configName, setConfigName] = useState("");
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const { configMapId } = outputConfig.selectedOutputMapping;
  const [SelectableData, setSelectableData] = useState<OutputConfigBandMap[]>([]);
  const [inData, setInData] = useState<OutputConfigBandMap[]>([]);

  /**
   * Action when config name value is changed
   * @param e React synthetic event object
   */
  const onChange = (e: React.SyntheticEvent<any, Event>): void => {
    setConfigName(e.currentTarget.value);
  };

  /**
   * Action when valid data is available from form
   * @param data Data from popup form
   */
  const onPayloadDataAvailable = (data: OutputConfigBandMap[]): void => {
    setInData(data);
  };

  const isConfigNameValid = (): boolean => !!(configName.trim());
  const isTagsValid = (value: number): boolean => value > 0;

  /**
   * Build selectable data for form
   */
  const buildSelectableData = (): void => {
    const newData: OutputConfigBandMap[] = data.map((item: BandFormTypes) => {
      return {
        configName: "",
        tag: "",
        parentMapId: configMapId,
        bandGuid: item.bandGuid,
        name: item.name,
        includeInOutput: false,
        isBandName: false
      };
    });
    setSelectableData(newData);
  };

  /**
   * Build only when data changes
   */
  useEffect(() => {
    buildSelectableData();
  }, [data]);

  /**
   * Any change to config name or checkbox selection in form has to be handled
   */
  useEffect(() => {
    const outPayloadData = inData.map((item: OutputConfigBandMap) => {
      return {
        configName: configName,
        tag: item.tag,
        parentMapId: configMapId,
        bandGuid: item.bandGuid,
        name: item.name,
        includeInOutput: item.includeInOutput,
        isBandName: item.isBandName
      };
    });
    const isSaveValid = isConfigNameValid() && isTagsValid(outPayloadData.length);
    dispatch(OutputConfigSlice.actions.setEnableMappingPopupSave(isSaveValid));
    if (isSaveValid) {
      dispatch(OutputConfigSlice.actions.setOutputMappingPopupBandData(outPayloadData));
    }
  }, [configName, inData]);

  return (
    <>
      <Form>
        <FormSection
          id="ebt-outputConfig-config-mappingAgeForm">
          <FormElement
            labelText={translate("OutputConfig.OutputMappingAgeForm.Name")}
            mandatory={true}
            id="ebt-outputConfig-config-mappingAgeForm-formElement"
          >
            <Input
              id="ebt-outputConfig-config-mappingAgeForm-bandName"
              type={InputType.Text}
              name="bandName"
              value={configName}
              onChange={onChange}
            />
          </FormElement>
        </FormSection>
      </Form>
      {
        SelectableData &&
        SelectableData.length > 0 &&
        <OutputBandingMappingFormView
          configName={configName}
          data={data}
          SelectableData={SelectableData}
          onPayloadDataAvailable={onPayloadDataAvailable}
        />
      }
    </>
  );
};
